// ============================================================
// TATHYA DMS - Public Shareable Link View
// Version: 1.5.0
// Date: 2026-04-24
// Change (v1.1): Multi-term AND search + CONSAM rebrand.
// Change (v1.2): Totals row (Clause 7.1.2).
// Change (v1.3): PDF download icon per row (CEO Clause 6.1).
// Change (v1.4): Removed the hard-coded legacy PDF column.
//         PDF columns are now proper data_type='pdf' columns and
//         are rendered inline inside the normal cols.map — so
//         multi-PDF DBs show one column per PDF column, not a
//         single "PDF" catch-all tacked on at the end.
// Change (v1.5): Owner name + email shown in the header, same
//         👤 Name · email style used on the My Databases card.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import TCLogo from '../auth/TCLogo';

// Multi-term matcher. Space-separates the query into terms; a row
// passes when EVERY term is found in SOME cell (AND across terms,
// OR across cells). Same logic duplicated in DatabaseView.js — keep
// them in sync if the semantics ever change.
const matchesSearch = (row, search) => {
  if (!search || !search.trim()) return true;
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every(term =>
    row.DbCells?.some(cell =>
      (cell?.value ?? '').toString().toLowerCase().includes(term)
    )
  );
};

// Totals-row support (Clause 7.1.2). Same helpers as DatabaseView.
const SUMMABLE_TYPES = ['currency', 'number', 'formula'];

const toNumeric = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s === '#ERR') return null;
  const cleaned = s.replace(/,/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

const formatSum = (n) => {
  if (n === null || n === undefined) return '';
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const computeTotals = (rows, columns) => {
  const totals = {};
  const summable = columns.filter(c => SUMMABLE_TYPES.includes(c.data_type));
  for (const col of summable) {
    let sum = 0;
    let any = false;
    for (const row of rows) {
      const cell = row.DbCells?.find(c => c.column_id === col.id);
      const n = toNumeric(cell?.value);
      if (n !== null) { sum += n; any = true; }
    }
    totals[col.id] = any ? sum : null;
  }
  return totals;
};

const PublicView = () => {
  const { org_slug, db_number } = useParams();
  const [database, setDatabase] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Public endpoint - no auth required
        const res = await fetch(`/api/public/${org_slug}/${db_number}`);
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Not found');
        }
        const data = await res.json();
        setDatabase(data.database);
        setRows(data.rows);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [org_slug, db_number]);

  const getCellValue = (row, colId) => {
    const cell = row.DbCells?.find(c => c.column_id === colId);
    return cell?.value || '';
  };

  // Find an uploaded PDF for a (row, pdf-column) pair. Returns the
  // DbCellFile object or null. The public GET route now returns
  // row.DbCellFiles as part of each row (see public.js v2.0), one
  // entry per (row_id, column_id) that has a file.
  const getCellFile = (row, colId) =>
    row.DbCellFiles?.find(f => f.column_id === colId) || null;

  // Trigger the public Excel download. The browser fetches the
  // new /export endpoint (gated by active shared link) and saves
  // the XLSX blob via a synthetic <a download> click. Same pattern
  // as the authenticated grid's Export button — just no auth
  // header, since this is the public route.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetch(
        `/api/public/${org_slug}/${db_number}/export`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Export failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${database?.db_number}-${database?.name}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filteredRows = rows.filter(row => matchesSearch(row, search));

  if (loading) return (
    <div style={styles.center}>
      <TCLogo size={48} />
      <p style={{marginTop:'16px',color:'#595959'}}>Loading...</p>
    </div>
  );

  if (error) return (
    <div style={styles.center}>
      <TCLogo size={48} />
      <h2 style={{color:'#C0392B',marginTop:'16px'}}>Access Denied</h2>
      <p style={{color:'#595959',marginTop:'8px'}}>{error}</p>
    </div>
  );

  const cols = database?.DbColumns || [];

  // Totals row (Clause 7.1.2): sum currency/number/formula columns.
  // Both overall and filtered-subtotal when a search is active.
  const hasSummableColumn = cols.some(c => SUMMABLE_TYPES.includes(c.data_type));
  const totalsAll = hasSummableColumn ? computeTotals(rows, cols) : {};
  const totalsFiltered = hasSummableColumn && search
    ? computeTotals(filteredRows, cols)
    : null;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <TCLogo size={40} />
          <div>
            <div style={styles.dbNumber}>{database?.db_number}</div>
            <h1 style={styles.title}>{database?.name}</h1>
            {database?.owner && (
              <div style={styles.ownerRow}>
                <span style={styles.ownerIcon}>👤</span>
                <span style={styles.ownerName}>{database.owner.name}</span>
                <span style={styles.ownerEmail}>{database.owner.email}</span>
              </div>
            )}
            <p style={styles.meta}>
              {cols.length} columns · {rows.length} rows ·
              <span style={styles.orgBadge}>{org_slug.toUpperCase()}</span>
            </p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button
            onClick={handleExport}
            disabled={exporting}
            style={styles.exportBtn}
            title="Download as Excel"
          >
            {exporting ? 'Preparing…' : '↓ Export Excel'}
          </button>
          <div style={styles.readOnlyBadge}>
            🔒 Read Only
          </div>
        </div>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="🔍 Search — space-separated words, all must match (e.g. priya mumbai)"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={styles.search}
      />

      {/* Grid */}
      <div style={styles.gridWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.thNum}>#</th>
              {cols.map(col => (
                <th key={col.id} style={styles.th}>
                  {col.label}
                  <span style={styles.typeTag}>{col.data_type}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={cols.length + 1} style={styles.empty}>
                  {search ? 'No results found' : 'No records'}
                </td>
              </tr>
            ) : (
              filteredRows.map((row, idx) => (
                <tr key={row.id} style={{
                  ...styles.tr,
                  background: idx % 2 === 0 ? 'white' : '#fafafa'
                }}>
                  <td style={styles.tdNum}>{idx + 1}</td>
                  {cols.map(col => {
                    // PDF columns render an icon-link when the row
                    // has an uploaded file for this column, or a
                    // greyed dash when empty. Everything else uses
                    // the text cell value.
                    if (col.data_type === 'pdf') {
                      const file = getCellFile(row, col.id);
                      return (
                        <td key={col.id} style={{...styles.td, textAlign:'center'}}>
                          {file ? (
                            <a
                              href={`/api/public/${org_slug}/pdf/${row.id}/${col.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={file.original_name}
                              style={styles.pdfLink}
                            >
                              📄
                            </a>
                          ) : (
                            <span style={styles.emptyDash}>—</span>
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={col.id} style={styles.td}>
                        {getCellValue(row, col.id)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
          {hasSummableColumn && filteredRows.length > 0 && (
            <tfoot>
              <tr style={styles.totalsRow}>
                <td style={styles.totalsLabel}>Total</td>
                {cols.map(col => {
                  const summable = SUMMABLE_TYPES.includes(col.data_type);
                  if (!summable) {
                    return <td key={col.id} style={styles.totalsCell}></td>;
                  }
                  const allVal = totalsAll[col.id];
                  const filtVal = totalsFiltered ? totalsFiltered[col.id] : null;
                  if (allVal === null || allVal === undefined) {
                    return <td key={col.id} style={styles.totalsCell}></td>;
                  }
                  return (
                    <td key={col.id} style={styles.totalsCell}>
                      {totalsFiltered !== null ? (
                        <div style={styles.totalsStack}>
                          <div style={styles.totalsMain}>{formatSum(allVal)}</div>
                          <div style={styles.totalsSub}>
                            Filtered: {formatSum(filtVal)}
                          </div>
                        </div>
                      ) : (
                        <div style={styles.totalsMain}>{formatSum(allVal)}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <span>{filteredRows.length} of {rows.length} rows</span>
        <div style={styles.poweredBy}>
          Powered by <strong>CONSAM DMS</strong> · © 2026 CONSAM
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', background: '#f5f7fa', padding: '24px' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  headerLeft: { display: 'flex', gap: '16px', alignItems: 'center' },
  dbNumber: { fontSize: '11px', color: '#2E75B6', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '4px' },
  title: { fontSize: '20px', fontWeight: '700', color: '#1B3A5C' },
  meta: { fontSize: '13px', color: '#595959', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
  orgBadge: { background: '#D6E4F0', color: '#1B3A5C', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },

  // Owner row (v1.5) — same visual pattern as DatabaseList card.
  ownerRow: {
    display: 'flex', alignItems: 'center', gap: '5px',
    marginTop: '4px', marginBottom: '4px', flexWrap: 'wrap'
  },
  ownerIcon:  { fontSize: '11px' },
  ownerName:  { fontSize: '12px', color: '#1B3A5C', fontWeight: '600' },
  ownerEmail: { fontSize: '11px', color: '#888' },
  readOnlyBadge: { background: '#FFF2CC', color: '#B7600A', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600' },

  // Header-right cluster (v1.5): Export button + Read-Only badge
  // grouped together so they don't compete for the top-right space.
  headerRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  exportBtn: {
    padding: '6px 14px', fontSize: '13px', fontWeight: '600',
    background: '#2E75B6', color: '#fff', border: 'none',
    borderRadius: '6px', cursor: 'pointer'
  },
  search: { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', marginBottom: '16px' },
  gridWrapper: { background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  thNum: { width: '50px', padding: '10px 8px', background: '#1B3A5C', color: 'white', fontWeight: '600', textAlign: 'center', fontSize: '12px', position: 'sticky', top: 0 },
  th: { padding: '10px 12px', background: '#1B3A5C', color: 'white', fontWeight: '600', textAlign: 'left', fontSize: '13px', borderLeft: '1px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, whiteSpace: 'nowrap' },
  typeTag: { marginLeft: '6px', fontSize: '10px', opacity: 0.6, fontWeight: '400', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  tdNum: { padding: '8px', textAlign: 'center', color: '#595959', fontSize: '12px', background: '#fafafa', borderRight: '1px solid #f0f0f0' },
  td: { padding: '8px 12px', borderLeft: '1px solid #f0f0f0', fontSize: '13px', color: '#1B3A5C' },
  empty: { textAlign: 'center', padding: '40px', color: '#595959' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', fontSize: '12px', color: '#595959' },
  poweredBy: { fontSize: '12px', color: '#595959' },

  // Totals row (Clause 7.1.2)
  totalsRow: { background: '#EBF3FB', borderTop: '2px solid #1B3A5C' },
  totalsLabel: {
    padding: '10px 8px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#1B3A5C',
    background: '#D6E4F0',
    borderRight: '1px solid #c0d0e0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  totalsCell: {
    padding: '8px 12px',
    borderLeft: '1px solid #c0d0e0',
    fontSize: '13px',
    color: '#1B3A5C',
    textAlign: 'right',
    fontWeight: '600'
  },
  totalsStack: { display: 'flex', flexDirection: 'column', gap: '2px' },
  totalsMain: { fontWeight: '700' },
  totalsSub:  { fontSize: '11px', fontWeight: '500', color: '#595959' },
  pdfLink: { fontSize: '18px', textDecoration: 'none', lineHeight: 1 },
  emptyDash: { color: '#BBB', fontSize: '14px' }
};

export default PublicView;
